import { Select } from "@sc/ui";
import { BASE_VERTICAL_PADDING_LARGE_IN_REM } from "constants/index";
import dayjs from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import React, { useMemo, useRef } from "react";
import styled from "styled-components/macro";
import {
  buildTimezoneOptions,
  HOUR_OPTIONS,
  HourMinuteOption,
  MINUTE_OPTIONS,
  Period,
  PERIOD_OPTIONS,
  PERIODS,
  TimeZoneValue,
} from "./utils";

dayjs.extend(utcPlugin);

export interface TimePickerProps {
  id: string;
  hours: number | null;
  minutes: number | null;
  period?: Period | null;
  timeZone?: TimeZoneValue | null;
  timeZones?: TimeZoneValue[];
  onHoursChange: (selectedItem: HourMinuteOption) => void;
  onMinutesChange: (selectedItem: HourMinuteOption) => void;
  onTimePeriodChange: (period: Period) => void;
  onTimeZoneChange?: (timeZone: TimeZoneValue) => void;
  className?: string;
}

/**
 * Presentational time picker with hour, minute, AM/PM, and optional timezone
 * dropdowns. This is the lowest-level component — all variant components
 * delegate to this.
 *
 * Use this directly when you already have pre-parsed 12-hour time parts
 * (hours, minutes, period) and want to control state externally.
 *
 * The timezone dropdown uses a searchable Select with US zones pinned to the
 * top. It only renders when `onTimeZoneChange` is provided.
 */
const TimePicker = ({
  id,
  hours,
  minutes,
  period = PERIODS.AM,
  timeZone,
  timeZones = [],
  onHoursChange,
  onMinutesChange,
  onTimePeriodChange,
  onTimeZoneChange,
  className,
}: TimePickerProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const tzOptions = useMemo(() => buildTimezoneOptions(timeZones), [timeZones]);

  const resolvedTimezone: TimeZoneValue | undefined = useMemo(() => {
    if (timeZone && timeZones.includes(timeZone)) return timeZone;
    return timeZones[0] ?? undefined;
  }, [timeZone, timeZones]);

  /** Render Select dropdowns inside our own DOM tree so option clicks don't
   *  bubble to parent popover/modal click-outside handlers. Has no effect
   *  if this component is not rendered in a popover, tooltip, or modal. */
  const popupContainer = () => rootRef.current || document.body;

  const hourSearchRef = useRef("");
  const hourDidSelectRef = useRef(false);

  const minuteSearchRef = useRef("");
  const minuteDidSelectRef = useRef(false);

  /**
   * When a user inputs an hour value, commit the closest matching value onBlur so a user can input and tab through fields.
   */
  const commitHourSearch = () => {
    if (hourDidSelectRef.current) {
      hourDidSelectRef.current = false;
      hourSearchRef.current = "";
      return;
    }
    const search = hourSearchRef.current;
    if (search) {
      // Exact label match first (e.g. "1" … "12"), then numeric parse
      // fallback for zero-padded input like "05" → value 5.
      const matched =
        HOUR_OPTIONS.find((o) => o.label === search) ??
        HOUR_OPTIONS.find((o) => o.value === parseInt(search, 10));
      if (matched && matched.value !== hours) {
        onHoursChange({ value: matched.value, label: matched.label });
      }
    }
    hourSearchRef.current = "";
  };

  /**
   * When a user inputs a minute value, commit the closest matching value onBlur so a user can input and tab through fields.
   */
  const commitMinuteSearch = () => {
    if (minuteDidSelectRef.current) {
      minuteDidSelectRef.current = false;
      minuteSearchRef.current = "";
      return;
    }
    const search = minuteSearchRef.current;
    if (search) {
      // Try exact label match first, then zero-padded (e.g. "5" → "05")
      const matched =
        MINUTE_OPTIONS.find((o) => o.label === search) ??
        MINUTE_OPTIONS.find((o) => o.label === String(search).padStart(2, "0"));
      if (matched && matched.value !== minutes) {
        onMinutesChange({ value: matched.value, label: matched.label });
      }
    }
    minuteSearchRef.current = "";
  };

  const handleHoursChange = (val: number) => {
    hourDidSelectRef.current = true;
    const label = HOUR_OPTIONS.find((o) => o.value === val)?.label ?? "";
    return onHoursChange({ value: val, label });
  };

  const handleMinutesChange = (val: number) => {
    minuteDidSelectRef.current = true;
    const label = MINUTE_OPTIONS.find((o) => o.value === val)?.label ?? "";
    return onMinutesChange({ value: val, label });
  };

  const handlePeriodChange = (val: Period) => {
    onTimePeriodChange(val);
  };

  const handleTimezoneChange = (val: TimeZoneValue) => {
    onTimeZoneChange?.(val);
  };

  return (
    <Root ref={rootRef} className={className} data-testid={`timepicker-root`}>
      <TimeFields>
        <HourFieldWrapper>
          <Select
            id={`${id}-hours`}
            data-testid={`timepicker-hours`}
            allowClear={false}
            getPopupContainer={popupContainer}
            options={HOUR_OPTIONS}
            showSearch
            value={hours ?? undefined}
            onSearch={(val: string) => {
              hourSearchRef.current = val;
            }}
            onBlur={commitHourSearch}
            onChange={handleHoursChange}
          />
        </HourFieldWrapper>
        <MinuteFieldWrapper>
          <Select
            id={`${id}-minutes`}
            data-testid={`timepicker-minutes`}
            allowClear={false}
            getPopupContainer={popupContainer}
            options={MINUTE_OPTIONS}
            showSearch
            value={minutes ?? undefined}
            onSearch={(val: string) => {
              minuteSearchRef.current = val;
            }}
            onBlur={commitMinuteSearch}
            onChange={handleMinutesChange}
          />
        </MinuteFieldWrapper>
        <PeriodFieldWrapper>
          <Select
            id={`${id}-period`}
            data-testid={`timepicker-period`}
            allowClear={false}
            getPopupContainer={popupContainer}
            options={PERIOD_OPTIONS}
            value={period ?? PERIODS.AM}
            onChange={handlePeriodChange}
          />
        </PeriodFieldWrapper>
      </TimeFields>
      {onTimeZoneChange && (
        <TimezoneWrapper>
          <Select
            id={`${id}-time-zone`}
            data-testid={`timepicker-time-zone`}
            allowClear={false}
            getPopupContainer={popupContainer}
            options={tzOptions}
            showSearch
            value={resolvedTimezone}
            onChange={handleTimezoneChange}
          />
        </TimezoneWrapper>
      )}
    </Root>
  );
};

export default TimePicker;

const Root = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const TimeFields = styled.div`
  display: flex;

  &:not(:only-child) {
    margin-right: 4px;
    margin-bottom: ${BASE_VERTICAL_PADDING_LARGE_IN_REM}rem;
  }
`;

const HourFieldWrapper = styled.div`
  flex: 1;
  margin-left: 0;
  margin-right: 2px;
`;

const MinuteFieldWrapper = styled.div`
  flex: 1;
  margin-left: 2px;
  margin-right: 2px;
`;

const PeriodFieldWrapper = styled.div`
  flex: 1;
  margin-left: 2px;
  margin-right: 0;
`;

const TimezoneWrapper = styled.div`
  width: 100%;
`;
